import { Link, Text } from '@chakra-ui/react';
import { CTAButton } from '@gamma/ui/buttons';
import { ErrorBoundary } from '@gamma/ui/overlay';
import {
  CardPageCTA,
  CardPageHeading,
  CardPageIllustration,
} from '@gamma/ui/pages';
import { CognitoUserInterface } from '@investigator/shared/types/auth-types';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { CONSTANTS, ROUTES, URLS } from '../../../../../shared/constants';
import {
  useAcceptPendingTerms,
  useLogOperationCall,
} from '../../../../../shared/hooks';
import { i18n } from '../../../../../shared/localization';
import {
  getUserId,
  getUserScopedKey,
} from '../../../../../shared/providers/shared/utils';

export interface SSOWelcomeProps {
  user?: CognitoUserInterface;
}

/**
 * SSO Welcome component for new users
 * Handles EULA and Humio Terms & Conditions acceptance flow
 * @param {SSOWelcomeProps} props - Component props
 * @returns {JSX.Element} Welcome page with terms acceptance
 */
export const SSOWelcome = ({ user }: SSOWelcomeProps) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const { handleSubmit } = useForm();
  const navigate = useNavigate();
  const logOperationCall = useLogOperationCall();
  const { title, cta, accept, privacyPolicy } = i18n.pages.registration.welcome;
  const { home } = ROUTES;

  const { acceptEulaNow, acceptHumioNow } = useAcceptPendingTerms();

  /**
   * Handles form submission for terms acceptance
   * Processes EULA and Humio TC acceptance, then navigates to home
   * If no session exists, redirects to IdP for authentication
   */
  const onSubmit = async () => {
    setIsProcessing(true);
    try {
      if (user?.signInUserSession) {
        const id_token = user?.signInUserSession.idToken?.jwtToken;

        // Set user-scoped localStorage key so EULA mutation will fire
        const userId = getUserId(user);
        localStorage.setItem(
          getUserScopedKey('accept_privacy_policy', userId),
          'true',
        );

        // Accept EULA
        const eulaRes = await acceptEulaNow();

        if (eulaRes.success) {
          localStorage.removeItem('accept_privacy_policy');
          localStorage.setItem('isEulaAccepted', 'true');
          // Clear the session check flag so it doesn't check again
          sessionStorage.setItem('eula_check_complete', 'true');

          try {
            await logOperationCall('PRIVACY_POLICY_ACKNOWLEDGED', 'USER');
          } catch (e) {
            console.error('[SSOWelcome] logOperationCall error:', e);
          }
        }

        // Accept Humio Terms & Conditions
        const humioRes = await acceptHumioNow({
          idToken: id_token,
          source: 'SSOWelcome',
        });

        if (humioRes.success) {
          localStorage.removeItem('humio_tc_accepted');
        }

        // Navigate to the intended destination or home
        const postEulaRedirect = localStorage.getItem('post_eula_redirect');
        const nextFromStorage = localStorage.getItem('next');

        if (postEulaRedirect && postEulaRedirect !== '/') {
          localStorage.removeItem('post_eula_redirect');
          navigate(postEulaRedirect);
        } else if (nextFromStorage) {
          localStorage.removeItem('next');
          navigate(nextFromStorage);
        } else {
          navigate(home);
        }
      } else {
        // No session - set flags and redirect to IdP
        localStorage.setItem('accept_privacy_policy', 'true');
        localStorage.setItem('humio_tc_accepted', 'true');

        const {
          SSO: { domain, client_id, redirect_uri },
        } = CONSTANTS;
        const sso_domain = window.sessionStorage.getItem('sso_domain');
        const idpLoginPage = `https://${domain}/oauth2/authorize?idp_identifier=${sso_domain}&client_id=${client_id}&response_type=code&scope=aws.cognito.signin.user.admin+email+openid+profile&redirect_uri=${redirect_uri}`;
        window.location.href = idpLoginPage;
      }
    } catch (e) {
      console.error('[SSOWelcome] Error during terms acceptance process:', e);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ErrorBoundary page="Registration Welcome Page">
      <form
        data-testid="sso-welcome-card"
        id="eulaForm"
        onSubmit={handleSubmit(onSubmit)}
      >
        <CardPageHeading>
          {i18n.formatString(title, {
            appName: <strong>{i18n.app.name}</strong>,
          })}
        </CardPageHeading>
        <CardPageIllustration illustration="Welcome" />
        <Text color="gray.400">
          {i18n.formatString(accept, {
            privacyPolicy: (
              <Link
                data-testid="privacy-policy"
                href={URLS.CORELIGHT_PRIVACY_POLICY}
                color="text.link"
                textDecoration="underline"
                isExternal
                _focus={{ outline: 'none' }}
              >
                {privacyPolicy}
              </Link>
            ),
          })}
        </Text>
      </form>
      <CardPageCTA>
        <CTAButton
          data-testid="continue-cta"
          type="submit"
          form="eulaForm"
          isLoading={isProcessing}
          disabled={isProcessing}
        >
          {cta}
        </CTAButton>
      </CardPageCTA>
    </ErrorBoundary>
  );
};
